import { useEffect, useState } from "react";
import { KeyRound, ScanFace, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { ConfirmDialog, Field } from "./common";
import { AuthImage } from "./AuthImage";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";

export const AksesPortalCard = ({ anak, onSaved }) => {
  const [f, setF] = useState({ portal_aktif: false, pin: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  useEffect(() => setF({ portal_aktif: anak.portal_aktif, pin: "", email: anak.email || "", password: "" }), [anak]);

  const save = async (extra = {}) => {
    if (f.pin && !/^\d{6}$/.test(f.pin)) return toast.error("PIN harus 6 digit angka");
    setSaving(true);
    try {
      await api.put(`/anak/${anak.id}/akses`, { portal_aktif: f.portal_aktif, pin: f.pin || null, email: f.email || null, password: f.password || null, ...extra });
      toast.success("Akses portal disimpan");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const resetWajah = async () => {
    setResetOpen(false);
    try { await api.post(`/anak/${anak.id}/reset-wajah`); toast.success("Wajah direset. Anak akan daftar ulang saat login berikutnya."); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="mb-5 grid gap-4 lg:grid-cols-[1.4fr_1fr]" data-testid="akses-portal-card">
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-heading font-bold text-gray-900"><KeyRound className="h-4 w-4 text-primary" />Akses Portal Anak</p>
          <label className="flex items-center gap-2 text-sm font-medium">
            {f.portal_aktif ? "Aktif" : "Nonaktif"}
            <Switch data-testid="akses-portal-switch" checked={f.portal_aktif} onCheckedChange={(v) => setF({ ...f, portal_aktif: v })} />
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="PIN 6 digit" hint={anak.has_pin ? "PIN sudah diatur — isi untuk reset" : "Login dengan No. HP + PIN"}>
            <Input data-testid="akses-pin-input" inputMode="numeric" maxLength={6} className="h-10 font-mono tracking-widest" value={f.pin} placeholder="••••••" onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
          </Field>
          <Field label="Email (opsional)"><Input data-testid="akses-email-input" type="email" className="h-10" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Password" hint={anak.has_password ? "Sudah diatur — isi untuk ganti" : "Minimal 6 karakter"}>
            <Input data-testid="akses-password-input" type="password" className="h-10" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          </Field>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="akses-save-btn" disabled={saving} className="h-10 rounded-full px-6" onClick={() => save()}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan akses</Button>
          {anak.email && <Button data-testid="akses-hapus-email-btn" variant="ghost" disabled={saving} className="h-10 text-red-600" onClick={() => save({ email: null, password: null, hapus_email: true })}>Hapus email & password</Button>}
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <p className="mb-4 flex items-center gap-2 font-heading font-bold text-gray-900"><ScanFace className="h-4 w-4 text-primary" />Wajah referensi</p>
        {anak.face_registered ? (
          <div className="flex items-center gap-4">
            <AuthImage src={`/anak/${anak.id}/wajah/foto`} className="h-24 w-24 rounded-xl" testid="akses-face-photo" />
            <div className="space-y-2">
              <p className="text-xs text-gray-500">Didaftarkan {fmtDateTime(anak.face_registered_at)}</p>
              <Button data-testid="akses-reset-wajah-btn" variant="outline" className="h-9 rounded-full text-red-700 hover:bg-red-50" onClick={() => setResetOpen(true)}><RotateCcw className="mr-1.5 h-4 w-4" />Reset Wajah</Button>
            </div>
          </div>
        ) : <p data-testid="akses-face-empty" className="text-sm text-gray-500">Belum daftar wajah. Anak akan diminta daftar wajah saat login pertama.</p>}
      </div>
      <ConfirmDialog open={resetOpen} onOpenChange={setResetOpen} destructive title="Reset wajah?" description="Foto & data wajah referensi akan dihapus. Anak harus daftar wajah ulang saat login berikutnya." confirmLabel="Reset" onConfirm={resetWajah} testid="akses-reset-confirm" />
    </section>
  );
};
